/**
 * GatewayClient — typed client for CMHKAgentPlatform Gateway REST APIs.
 *
 * Auth: Federate OIDC id_token via X-Id-Token header (from cookie), plus the
 * access_token via X-Access-Token when present — see context/gatewayAuthHeaders.ts.
 * On 401: silent refresh (hidden iframe) → retry once → redirect to login.
 *
 * Usage:
 *   import { gatewayClient } from '../client/GatewayClient';
 *   const result = await gatewayClient.listWorkspace({ agent: 'rocky' });
 *   await gatewayClient.chatStream({ ... }, (msg) => console.log(msg));
 */

import { appConfig } from '../config';
import { refreshAuth } from '../context/AuthContext';
import { gatewayAuthHeaders } from '../context/gatewayAuthHeaders';

// ── Types ───────────────────────────────────────────────────────────────────

export interface WorkspaceFile {
  name: string;
  key: string;
  size: number;
  modified: string;
}

export interface WorkspaceListResponse {
  user: string;
  agent: string;
  prefix: string;
  folders: string[];
  files: WorkspaceFile[];
}

export interface WorkspaceListParams {
  agent: string;
  prefix?: string;
  recursive?: boolean;
  user?: string;
}

export interface WorkspaceFileResponse {
  user: string;
  agent: string;
  path: string;
  content_type: string;
  size: number;
  content: string;
}

// ── Chat Types ──────────────────────────────────────────────────────────────

export enum ChatMessageType {
  ESTABLISH_CONNECTION = 'establish_connection',
  AGENT_THOUGHT = 'agent_thought',
  AGENT_FINAL_RESPONSE = 'agent_final_response',
  USER_QUERY = 'user_query',
  ERROR = 'error',
  AUTH_ERROR = 'auth_error',
  SYSTEM = 'system',
  STATE_SNAPSHOT = 'state_snapshot',
}

export interface ChatMessageContent {
  userQuery: string | null;
  agentFinalResponse: string;
  agentThought: string | null;
  toolUsed: string | null;
  toolOutput: string | null;
}

export interface ChatMessage {
  messageId: string;
  messageType: ChatMessageType;
  messageContent: ChatMessageContent;
  conversationId: string;
  sessionId: string;
  timestamp: string;
  isStream: boolean;
  source: string;
  sourceType: string;
  isSideOutput: boolean;
}

export interface ChatRequest {
  sessionId: string;
  agent: string;
  messages: Array<{ role: string; content: string }>;
  context?: Record<string, string>;
  streaming?: boolean;
  media?: UploadedFile[];
}


// ── Upload Types ────────────────────────────────────────────────────────────

export interface UploadedFile {
  file_id: string;
  filename: string;
  mimetype: string;
  type: string;      // 'image' | 'document' | 'video'
  format: string;    // 'png' | 'pdf' | 'csv' etc.
  size: number;
  s3_uri: string;
  s3_bucket: string;
  s3_key: string;
}

export interface UploadResponse {
  files: UploadedFile[];
  errors: string[];
}


// ── Session/History Types ───────────────────────────────────────────────────

export interface SessionItem {
  session_id: string;
  agent_id: string;
  description: string | null;
  source: string | null;
  create_time: string;
  update_time: string;
}

export interface SessionListResponse {
  user_id: string;
  source: string;
  sessions: SessionItem[];
  count: number;
  next_token?: string;
}

export interface SessionMessage {
  message_id: string;
  message_type: string;
  message_role: string;
  message_content: string;
  conversation_id: string;
  agent_name: string;
  user_alias: string;
  create_time: string;
  update_time: string;
  feedback: string | null;
  // Structured tool-call arguments. Only populated on tool_use rows
  // (since runtime started writing this field). Older rows return null
  // — the historical view falls back to "tool name only" when absent,
  // matching the pre-fix display.
  tool_input?: Record<string, unknown> | null;
  /**
   * The tool call's own id (AG-UI `toolCallId`) — the real join key between this
   * row and anything else that knows about the call, including a gate's verdict
   * from `getGateHistory`. `message_id` above is a uuid4 the Runtime minted and
   * joins to nothing.
   *
   * Written by the Runtime's AG-UI persistence since 2026-09-28; absent on every
   * row written before that and on the legacy streaming path. So it is a fast
   * path, never a guarantee — `planToolCalls` falls back to matching on content
   * when it is missing.
   */
  tool_call_id?: string | null;
  /**
   * On a `user_query` sent by clicking a follow-up suggestion: the option id
   * (`<suggestion message_id>#<n>`). null / absent for typed messages.
   */
  follow_up_id?: string | null;
}

export interface SessionMessagesResponse {
  session_id: string;
  messages: SessionMessage[];
  count: number;
  next_token?: string;
}

export interface ListSessionsParams {
  source?: string;
  limit?: number;
  next_token?: string;
  user?: string;
  agent_id?: string;
}

export interface ListMessagesParams {
  limit?: number;
  order?: string;
  next_token?: string;
  /** Admin impersonation target (rocky-admin only). Mirrors ListSessionsParams.user. */
  user?: string;
}

/** One HITL gate a thread is currently parked on. Shapes match what the
 *  Runtime returns (see components/ag_ui_chat/pending_interrupts.py). */
export interface PendingInterrupt {
  /** AG-UI toolCallId — the addressing key used to answer the gate on resume. */
  tool_call_id: string;
  /** One of the registered gates (`HITL_TOOL_NAMES` in
   *  components/ChatPanel/agui/hitlToolNames.ts). */
  tool_name: string;
  /** The tool-call arguments, i.e. what the card renders (untruncated — read
   *  from the server-side interrupt state, not from DDB history). */
  arguments: Record<string, unknown>;
  interrupt_id: string;
}

export interface PendingInterruptsResponse {
  pending: PendingInterrupt[];
  activated: boolean;
  /** `agentcore_memory` = authoritative read (empty list ⇒ nothing pending);
   *  `unavailable` = could NOT read (do not treat as "nothing pending"). */
  store: 'agentcore_memory' | 'unavailable' | string;
}

/** One HITL gate this thread has already answered. */
export interface SettledGate {
  /** Real AG-UI toolCallId from the conversation (not a DynamoDB message id). */
  tool_call_id: string;
  tool_name: string;
  /** Untruncated tool-call arguments, read from the conversation. */
  arguments: Record<string, unknown>;
  /** Raw result string the operator's click produced, e.g. `{"approved":true}`. */
  result: string;
  /** Tool-result status: `success` | `error`. */
  status: string;
}

export interface GateHistoryResponse {
  /** In conversation order. The caller matches these positionally against its
   *  own DynamoDB history, so this ordering must be preserved. */
  gates: SettledGate[];
  store: 'agentcore_memory' | 'unavailable' | string;
}

// ── Error ───────────────────────────────────────────────────────────────────

export class GatewayApiError extends Error {
  constructor(
    public readonly statusCode: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'GatewayApiError';
  }
}

// ── Client ──────────────────────────────────────────────────────────────────

export class GatewayClient {
  private baseUrl: string;
  private papiCache: Map<string, { data: PapiEmployeeResponse; expiry: number }> = new Map();
  private static PAPI_CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

  constructor() {
    this.baseUrl = appConfig.gatewayUrl;
  }

  private buildHeaders(): Record<string, string> {
    return gatewayAuthHeaders();
  }

  /**
   * Shared request handler: auth, error parsing, 401 silent-refresh + retry.
   *
   * On 401: attempt silent token refresh via hidden iframe.
   * If refresh succeeds → retry the request once with the new token.
   * If refresh fails → fall back to full-page Federate redirect.
   */
  private async request<T>(method: string, url: string, body?: string): Promise<T> {
    const doFetch = async () => {
      const headers = this.buildHeaders();
      if (body) headers['Content-Type'] = 'application/json';
      return fetch(url, { method, headers, body, cache: 'no-store' });
    };

    let response = await doFetch();

    // 401 → silent refresh + one retry
    if (response.status === 401) {
      const newToken = await refreshAuth();
      if (newToken) {
        // Retry with the fresh token (buildHeaders will pick it up from cookie)
        response = await doFetch();
      }
      // If still 401 after retry (or refreshAuth triggered redirect), bail
      if (response.status === 401) {
        throw new GatewayApiError(401, 'unauthorized', 'Session expired');
      }
    }

    if (!response.ok) {
      let errorBody: { error?: { code?: string; message?: string } };
      try {
        errorBody = await response.json();
      } catch {
        errorBody = {};
      }
      throw new GatewayApiError(
        response.status,
        errorBody.error?.code ?? 'unknown',
        errorBody.error?.message ?? `HTTP ${response.status}`,
      );
    }

    return response.json();
  }

  async get<T>(path: string, params?: Record<string, string>): Promise<T> {
    const url = new URL(`${this.baseUrl}${path}`);
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== '') url.searchParams.set(k, v);
      });
    }
    return this.request<T>('GET', url.toString());
  }

  async post<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>('POST', `${this.baseUrl}${path}`, JSON.stringify(body));
  }

  // ── Workspace API ───────────────────────────────────────────────────────

  async listWorkspace(params: WorkspaceListParams): Promise<WorkspaceListResponse> {
    const query: Record<string, string> = {
      agent: params.agent,
      prefix: params.prefix ?? '',
    };
    if (params.recursive) query.recursive = 'true';
    if (params.user) query.user = params.user;
    return this.get<WorkspaceListResponse>('/channels/portal/workspace', query);
  }

  /**
   * Fetch a workspace file via /channels/portal/workspace/file.
   * Returns JSON with content_type + content fields.
   */
  async getWorkspaceFile(agent: string, path: string, user?: string): Promise<WorkspaceFileResponse> {
    return this.get<WorkspaceFileResponse>('/channels/portal/workspace/file', {
      agent,
      path,
      ...(user ? { user } : {}),
    });
  }

  /**
   * Download a workspace file as a Blob (for binary/non-renderable files).
   */
  async downloadWorkspaceFile(agent: string, path: string, user?: string): Promise<Blob> {
    const url = new URL(`${this.baseUrl}/channels/portal/workspace/file`);
    url.searchParams.set('agent', agent);
    url.searchParams.set('path', path);
    if (user) url.searchParams.set('user', user);
    const headers = this.buildHeaders();
    const resp = await fetch(url.toString(), { headers });
    if (!resp.ok) throw new Error(`Download failed: ${resp.status}`);
    return resp.blob();
  }


  // ── File Upload API ─────────────────────────────────────────────────────

  /**
   * Upload files to S3 via Gateway.
   *
   * @param files - File objects from <input type="file">
   * @param sessionId - Session ID for S3 key organization
   * @returns Upload response with S3 metadata for each file
   */
  async uploadFiles(files: File[], sessionId: string): Promise<UploadResponse> {
    const formData = new FormData();
    formData.append('session_id', sessionId);
    for (const file of files) {
      formData.append('file', file);
    }

    const doUpload = () => fetch(`${this.baseUrl}/channels/rocky/upload`, {
      method: 'POST',
      headers: this.buildHeaders(),
      body: formData,
    });

    let response = await doUpload();

    // 401 → silent refresh + one retry (mirrors request() pattern)
    if (response.status === 401) {
      const newToken = await refreshAuth();
      if (newToken) {
        response = await doUpload();
      }
      if (response.status === 401) {
        throw new GatewayApiError(401, 'unauthorized', 'Session expired');
      }
    }

    if (!response.ok) {
      let errorBody: { error?: { code?: string; message?: string } };
      try {
        errorBody = await response.json();
      } catch {
        errorBody = {};
      }
      throw new GatewayApiError(
        response.status,
        errorBody.error?.code ?? 'unknown',
        errorBody.error?.message ?? `Upload failed (HTTP ${response.status})`,
      );
    }

    return response.json();
  }

  // ── Chat Streaming API ────────────────────────────────────────────────

  /**
   * Send a chat message and stream SSE responses.
   *
   * Follows the same pattern as AgentHubPortal's chatStreaming():
   * - POST /channels/portal/chat with SSE response
   * - Parse "data: {...}" lines separated by "\n\n"
   * - Dispatch parsed ChatMessage objects to onMessage callback
   * - On 401: silent refresh → retry once → emit AUTH_ERROR if still failing
   * - On 403: immediately emit AUTH_ERROR (token refresh won't change permissions)
   *
   * @param request - Chat request payload
   * @param onMessage - Callback for each parsed SSE message
   * @param onError - Optional error callback
   * @param signal - Optional AbortSignal to cancel the stream
   */
  async chatStream(
    request: ChatRequest,
    onMessage: (msg: ChatMessage) => void,
    onError?: (error: Error) => void,
    signal?: AbortSignal,
    onRawChunk?: (chunk: string) => void,
  ): Promise<void> {
    const url = `${this.baseUrl}/channels/portal/chat`;
    let retryCount = 0;

    const doStreamFetch = async () => {
      const headers = this.buildHeaders();
      headers['Content-Type'] = 'application/json';
      headers['Accept'] = 'text/event-stream';

      return fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
        signal,
      });
    };

    // Retry loop: at most 1 retry after silent refresh
    while (retryCount <= 1) {
      try {
        const response = await doStreamFetch();

        // 401 → attempt silent refresh + retry once
        if (response.status === 401) {
          if (retryCount < 1) {
            retryCount += 1;
            try {
              const newToken = await refreshAuth();
              if (newToken) {
                // Retry with fresh token
                continue;
              }
            } catch {
              // refreshAuth failed (redirect triggered) — emit AUTH_ERROR and stop
            }
          }
        }

        // Emit AUTH_ERROR for both 401 (after failed refresh) and 403
        if (response.status === 401 || response.status === 403) {
          const authError = new Error(`Authentication failed (HTTP ${response.status})`);
          onError?.(authError);
          onMessage({
            messageId: '',
            messageType: ChatMessageType.AUTH_ERROR,
            messageContent: {
              userQuery: null,
              agentFinalResponse: `Authentication error: ${authError.message}. Please refresh the page to re-login.`,
              agentThought: null,
              toolUsed: null,
              toolOutput: null,
            },
            conversationId: '',
            sessionId: request.sessionId,
            timestamp: new Date().toISOString(),
            isStream: false,
            source: 'system',
            sourceType: 'error',
            isSideOutput: false,
          });
          return;
        }

        if (!response.ok) {
          let errMsg = `HTTP ${response.status}`;
          try {
            const errBody = await response.json();
            errMsg = errBody?.error?.message ?? errMsg;
          } catch { /* ignore */ }
          throw new Error(errMsg);
        }

        // Read SSE stream
        const reader = response.body?.getReader();
        if (!reader) throw new Error('No response body');

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;

          const decoded = decoder.decode(value, { stream: true });
          if (onRawChunk) onRawChunk(decoded);
          buffer += decoded;

          // Split by SSE event boundary
          const events = buffer.split('\n\n');
          // Keep last (possibly incomplete) chunk in buffer
          buffer = events.pop() ?? '';

          for (const event of events) {
            if (!event.trim()) continue;
            this.processSSEEvent(event, onMessage);
          }
        }

        // Flush any remaining bytes held by the decoder from { stream: true }
        buffer += decoder.decode();

        // Flush remaining buffer
        if (buffer.trim()) {
          this.processSSEEvent(buffer, onMessage);
        }

        // Success — exit retry loop
        return;
      } catch (err) {
        if (signal?.aborted) return;
        const error = err instanceof Error ? err : new Error(String(err));
        if (onError) {
          onError(error);
        }
        // Emit an error message so UI can display it
        onMessage({
          messageId: '',
          messageType: ChatMessageType.ERROR,
          messageContent: {
            userQuery: null,
            agentFinalResponse: `Stream error: ${error.message}`,
            agentThought: null,
            toolUsed: null,
            toolOutput: null,
          },
          conversationId: '',
          sessionId: request.sessionId,
          timestamp: new Date().toISOString(),
          isStream: false,
          source: 'system',
          sourceType: 'error',
          isSideOutput: false,
        });
        return;
      }
    }
  }

  /** Parse a single SSE event block and dispatch to onMessage. */
  private processSSEEvent(
    eventText: string,
    onMessage: (msg: ChatMessage) => void,
  ): void {
    const lines = eventText.split('\n');
    let data = '';

    for (const line of lines) {
      if (line.startsWith('data: ')) {
        // Accumulate data lines (SSE spec allows multi-line data)
        data += (data ? '\n' : '') + line.slice(6);
      }
    }

    if (!data) return;

    try {
      const parsed = JSON.parse(data) as ChatMessage;
      onMessage(parsed);
    } catch (ex) {
      console.warn('[GatewayClient] Failed to parse SSE data:', ex, data);
    }
  }

  // ── Session History API ─────────────────────────────────────────────────

  async listSessions(params?: ListSessionsParams): Promise<SessionListResponse> {
    const query: Record<string, string> = {};
    if (params?.source) query.source = params.source;
    if (params?.limit) query.limit = String(params.limit);
    if (params?.next_token) query.next_token = params.next_token;
    if (params?.user) query.user = params.user;
    if (params?.agent_id) query.agent_id = params.agent_id;
    return this.get<SessionListResponse>('/channels/rocky/sessions', query);
  }


  // ── PAPI Proxy API ────────────────────────────────────────────────────

  /**
   * Fetch employee profile from PAPI via Gateway proxy.
   * Results are cached in memory for 30 minutes.
   */
  async getEmployee(
    login: string,
    expand: string = 'job,work-location',
    { skipCache = false }: { skipCache?: boolean } = {},
  ): Promise<PapiEmployeeResponse> {
    const cacheKey = `${login}:${expand}`;
    if (!skipCache) {
      const cached = this.papiCache.get(cacheKey);
      if (cached && Date.now() < cached.expiry) {
        return cached.data;
      }
    }

    const data = await this.get<PapiEmployeeResponse>(
      `/channels/rocky/papi/v2/employee/login:${encodeURIComponent(login)}`,
      { expand },
    );

    this.papiCache.set(cacheKey, {
      data,
      expiry: Date.now() + GatewayClient.PAPI_CACHE_TTL_MS,
    });

    return data;
  }

  /**
   * Convert a PAPI employee response to SenderInfo for chat requests.
   */
  static toSenderInfo(employee: PapiEmployeeResponse): SenderInfo {
    const b = employee.basicInfo;
    const j = employee.job;
    const w = employee.workLocation;
    return {
      user_id: b.personId || b.login,
      alias: b.login,
      name: `${b.firstName} ${b.lastName}`.trim(),
      email: b.primaryEmail || `${b.login}@amazon.com`,
      title: j?.businessTitle || b.businessTitle || '',
      org: b.costCenterName || '',
      office: w?.name || b.workspace || '',
    };
  }
  async listSessionMessages(
    sessionId: string,
    params?: ListMessagesParams,
  ): Promise<SessionMessagesResponse> {
    const query: Record<string, string> = {};
    if (params?.limit) query.limit = String(params.limit);
    if (params?.order) query.order = params.order;
    if (params?.next_token) query.next_token = params.next_token;
    if (params?.user) query.user = params.user;
    return this.get<SessionMessagesResponse>(
      '/channels/rocky/sessions/' + encodeURIComponent(sessionId) + '/messages',
      query,
    );
  }

  /**
   * Read the HITL approval gates a thread is currently parked on.
   *
   * Refresh recovery: a reloaded tab has lost the in-memory approval card, but
   * the run is still parked server-side. This asks the Runtime (via the AG-UI
   * channel's read-only pass-through) which gates are open so the card can be
   * rebuilt and answered.
   *
   * `store` distinguishes "no pending gate" (`agentcore_memory`, empty list)
   * from "could not read" (`unavailable`). Callers MUST NOT treat `unavailable`
   * as "nothing pending" — that would render a parked gate as already approved.
   */
  async getPendingInterrupts(
    threadId: string,
    agentName: string,
  ): Promise<PendingInterruptsResponse> {
    return this.post<PendingInterruptsResponse>(
      '/channels/agui/rocky/pending-interrupts',
      { threadId, agentName },
    );
  }

  /**
   * Read the HITL approval gates a thread has already answered, with verdicts.
   *
   * Used to rebuild the read-only audit cards after a reload. The verdict is not
   * in DynamoDB — `ag_ui_strands` never emits a tool result for frontend tools —
   * so it is read from the conversation the Runtime persisted.
   *
   * Heavier than `getPendingInterrupts` (rebuilds the conversation rather than
   * reading one state blob), so call it when rendering history, not on every
   * mount. Gates come back in conversation order and must be consumed in that
   * order: callers match them positionally against DynamoDB history, because the
   * two stores share no key.
   */
  async getGateHistory(threadId: string, agentName: string): Promise<GateHistoryResponse> {
    return this.post<GateHistoryResponse>(
      '/channels/agui/rocky/gate-history',
      { threadId, agentName },
    );
  }
}

// ── Singleton ───────────────────────────────────────────────────────────────

export const gatewayClient = new GatewayClient();

// ── PAPI Types ──────────────────────────────────────────────────────────────

export interface PapiBasicInfo {
  personId: string;
  lastName: string;
  firstName: string;
  login: string;
  badgeColor: string;
  businessTitle: string;
  primaryEmail: string;
  primaryPhone?: string;
  isManager: boolean;
  managerLogin?: string;
  managerName?: string;
  costCenterId?: string;
  costCenterName?: string;
  workspace?: string;
  tenureDays?: number;
  serviceDate?: string;
}

export interface PapiJob {
  businessTitle: string;
  jobLevel: string;
  jobFamilyName: string;
  hireDate?: string;
  isManager: boolean;
}

export interface PapiWorkLocation {
  id?: string;
  name?: string;
  buildingCode?: string;
  city?: string;
  country?: string;
  timeZoneId?: string;
}

export interface PapiEmployeeResponse {
  basicInfo: PapiBasicInfo;
  job?: PapiJob;
  workLocation?: PapiWorkLocation;
}

export interface SenderInfo {
  user_id: string;
  alias: string;
  name: string;
  email: string;
  title: string;
  org: string;
  office: string;
}
